import type { FilterOption } from '@models/Filter'
import type { Organisatie } from '@models/Organisatie'
import type { Projectbetrokkene } from '@models/Projectbetrokkene'

export {
  alleTypes,
  organisatieOptionsVoorTypes,
  beschikbareDelegerenden,
  organisatieOptionsVoorBetrokkene,
  isProjectbetrokkeneWijzigbaar,
  gebiedenVanVerantwoordelijke,
  groupProjectbetrokkenenByVerantwoordelijkeAndGebied,
  sortProjectbetrokkenenByType,
}

const alleTypes: Projectbetrokkene['type'][] = [
  'Aannemer',
  'Combi',
  'Netbeheerder',
  'GedelegeerdNetbeheerder',
]

/** Organisaties van de gegeven types, gesorteerd op type en naam. */
const organisatieOptionsVoorTypes = (
  organisaties: Organisatie[],
  types: Projectbetrokkene['type'][],
): Organisatie[] =>
  organisaties
    .filter((x) => types.includes(x.type))
    .sort(
      (a, b) =>
        a.type.localeCompare(b.type) ||
        a.organisatie.localeCompare(b.organisatie),
    )

/**
 * Een gedelegeerd netbeheerder mag per delegerende één keer worden opgevoerd,
 * dus alleen de delegerenden waarvoor hij nog niet betrokken is.
 */
const beschikbareDelegerenden = (
  organisatie: Organisatie,
  projectbetrokkenen: Projectbetrokkene[],
): string[] =>
  (organisatie.delegerenden ?? []).filter(
    (d) =>
      !projectbetrokkenen.some(
        (pb) =>
          pb.organisatie === organisatie.organisatie && pb.delegerende === d,
      ),
  )

const toegestaneTypesPerVerantwoordelijke: Partial<
  Record<Projectbetrokkene['type'], Projectbetrokkene['type'][]>
> = {
  Netbeheerder: ['Aannemer'],
  Combi: alleTypes.filter((type) => type !== 'Combi'),
}

/**
 * De organisaties die nog als betrokkene toegevoegd kunnen worden aan de
 * verantwoordelijkheidscontext van projectbetrokkenen.
 *
 * 1. inperken op organisatie type afhankelijk van verantwoordelijke type
 * 2. organisaties die al betrokken zijn vallen af,
 *    tenzij het een gedelegeerd netbeheerder is met beschikbare delegerenden.
 *
 */
const organisatieOptionsVoorBetrokkene = (
  organisaties: Organisatie[],
  projectbetrokkenen: Projectbetrokkene[],
): Organisatie[] => {
  // eerste in projectbetrokkenen is niet altijd de verantwoordelijke, vandaar find
  const verantwoordelijkeType = projectbetrokkenen.find(
    (pb) => pb.organisatie === pb.verantwoordelijke,
  )!.type // verantwoordelijke bestaat altijd, vandaar ! operator.

  const toegestaneTypes =
    toegestaneTypesPerVerantwoordelijke[verantwoordelijkeType] ?? alleTypes

  const mogelijkeOrganisaties = organisatieOptionsVoorTypes(
    organisaties,
    toegestaneTypes,
  )

  return mogelijkeOrganisaties.filter((org) => {
    const bestaandeOrganisaties = projectbetrokkenen.filter(
      (pb) => pb.organisatie === org.organisatie,
    )

    // als de mogelijke organisatie nog niet bestaat, mag deze toegevoegd worden
    if (bestaandeOrganisaties.length === 0) {
      return true
    }

    // als de mogelijke organisatie al bestaat,
    // mag alleen een nieuwe gedelegeerd netbeheerder toegevoegd worden als er nog beschikbare delegerenden zijn
    return (
      bestaandeOrganisaties.every(
        (pb) => pb.type === 'GedelegeerdNetbeheerder',
      ) && beschikbareDelegerenden(org, projectbetrokkenen).length > 0
    )
  })
}

/** Requirements for changing projectbetrokkenen of Aanvraag are still unclear, so for now only Hoofdleiding is editable */
const WIJZIGBARE_PROJECT_TYPES: Projectbetrokkene['projectType'][] = [
  'Hoofdleiding',
]

const isProjectbetrokkeneWijzigbaar = (
  projectType: Projectbetrokkene['projectType'],
) => WIJZIGBARE_PROJECT_TYPES.includes(projectType)

/**
 * De gebieden waarin de verantwoordelijke zelf is opgevoerd; alleen daar kan
 * een verantwoordelijkheidscontext bestaan.
 */
const gebiedenVanVerantwoordelijke = (
  projectbetrokkenen: Projectbetrokkene[],
): FilterOption[] =>
  projectbetrokkenen
    .filter(
      ({ organisatie, verantwoordelijke }) => organisatie === verantwoordelijke,
    )
    .reduce(
      (gebieden, { gebiedscode, gebiedscodeLabel }) =>
        gebieden.some(({ code }) => code === gebiedscode)
          ? gebieden
          : [...gebieden, { code: gebiedscode, label: gebiedscodeLabel }],
      [] as FilterOption[],
    )
    .sort((a, b) => a.label.localeCompare(b.label))

const groupProjectbetrokkenenByVerantwoordelijkeAndGebied = (
  projectbetrokkenen: Projectbetrokkene[],
) => {
  return projectbetrokkenen
    .sort((a, b) => a.gebiedscodeLabel.localeCompare(b.gebiedscodeLabel))
    .reduce(
      (acc, projectbetrokkene) => {
        const key =
          projectbetrokkene.verantwoordelijke +
          ' - ' +
          projectbetrokkene.gebiedscode

        if (!acc[key]) {
          acc[key] = []
        }

        acc[key].push(projectbetrokkene)
        acc[key] = sortProjectbetrokkenenByType(acc[key])

        return acc
      },
      {} as Record<string, Projectbetrokkene[]>,
    )
}

const sortProjectbetrokkenenByType = (
  projectbetrokkenen: Projectbetrokkene[],
): Projectbetrokkene[] => {
  return [...projectbetrokkenen].sort((a, b) => a.type.localeCompare(b.type))
}
